import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  RefreshRounded as RefreshIcon,
  DeleteOutlineRounded as DeleteIcon,
  CheckCircleOutlineRounded as ResolveIcon,
  AccessTimeRounded as ProgressIcon,
  BugReportOutlined as BugIcon,
} from '@mui/icons-material';
import apiClient from '../../api/client';
import PageHeader from '../../components/ui/PageHeader';
import PageBody from '../../components/ui/PageBody';
import StatTile from '../../components/ui/StatTile';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip, { Tone } from '../../components/ui/ToneChip';
import { EmptyState } from '../../components/ui/States';
import SettingsPanel from './SettingsPanel';
import { status as statusTokens } from '../../theme';

// Bug reports, reachable at last.
//
// This screen was written months ago, is 380 lines long, and had no route —
// the only way to read what testers had filed was to not have one. Two
// things were wrong with it beyond styling:
//
//  * It called bare `axios`, not the app's `apiClient`. No base URL and no
//    Authorization header, so every request would have gone to the web
//    origin and come back 404 or 401. It could never have worked, which is
//    consistent with it never having been opened.
//  * Its effect depended on `filters`, an object rebuilt every render, so
//    it re-fetched on every keystroke anywhere in the tree.

type BugReport = {
  _id: string;
  description: string;
  userName: string;
  userEmail: string;
  status: string;
  priority: string;
  category?: string;
  timestamp: string;
};

type BugReportStats = {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed?: number;
};

const STATUS_TONE: Record<string, Tone> = {
  open: 'danger',
  'in-progress': 'warn',
  resolved: 'ok',
  closed: 'neutral',
};

const PRIORITY_TONE: Record<string, Tone> = {
  critical: 'danger',
  high: 'warn',
  medium: 'info',
  low: 'neutral',
};

const BugReports: React.FC = () => {
  const [reports, setReports] = useState<BugReport[]>([]);
  const [stats, setStats] = useState<BugReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Held as separate primitives rather than one object: the old version put
  // them in a state object and listed it in the effect's deps, which made
  // the effect fire on every render.
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      const params = new URLSearchParams({ page: '1', limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);

      const [list, summary] = await Promise.all([
        apiClient.get<any, { data: BugReport[] }>(`/api/bug-reports?${params}`),
        apiClient.get<any, { data: BugReportStats }>('/api/bug-reports/stats').catch(() => null),
      ]);
      setReports(list?.data || []);
      if (summary) setStats(summary.data);
    } catch {
      setError('Could not load bug reports.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id: string, status: string) => {
    try {
      await apiClient.patch(`/api/bug-reports/${id}`, { status });
      load();
    } catch {
      setError('Could not update that report.');
    }
  };

  const remove = async (report: BugReport) => {
    const preview = report.description.slice(0, 60);
    if (!window.confirm(`Delete this bug report?\n\n“${preview}${report.description.length > 60 ? '…' : ''}”\n\nThis cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/bug-reports/${report._id}`);
      load();
    } catch {
      setError('Could not delete that report.');
    }
  };

  const filtered = !!statusFilter || !!priorityFilter;

  const columns: Column<BugReport>[] = [
    {
      key: 'priority',
      header: 'Priority',
      width: 110,
      render: (r) => <ToneChip tone={PRIORITY_TONE[r.priority] ?? 'neutral'} label={r.priority} />,
    },
    {
      key: 'description',
      header: 'What happened',
      width: 420,
      render: (r) => <Typography variant="body2">{r.description}</Typography>,
    },
    {
      key: 'user',
      header: 'Reported by',
      render: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{r.userName || 'Unknown'}</Typography>
          <Typography variant="caption" color="text.secondary">{r.userEmail}</Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <ToneChip tone={STATUS_TONE[r.status] ?? 'neutral'} label={r.status} />,
    },
    {
      key: 'date',
      header: 'Filed',
      render: (r) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(r.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
        </Typography>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 130,
      render: (r) => (
        <Stack direction="row" justifyContent="flex-end">
          <Tooltip title="Mark in progress">
            <span>
              <IconButton
                size="small"
                onClick={() => setStatus(r._id, 'in-progress')}
                disabled={r.status === 'in-progress'}
              >
                <ProgressIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Mark resolved">
            <span>
              <IconButton
                size="small"
                onClick={() => setStatus(r._id, 'resolved')}
                disabled={r.status === 'resolved'}
              >
                <ResolveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => remove(r)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <SettingsPanel />

      <PageBody>
        <PageHeader
          title="Bug reports"
          subtitle="What testers and drivers have filed from the apps."
          actions={
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          }
        >
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <TextField
              select label="Status" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in-progress">In progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>
            <TextField
              select label="Priority" value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All priorities</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </TextField>
            {filtered && (
              <Button onClick={() => { setStatusFilter(''); setPriorityFilter(''); }}>
                Clear filters
              </Button>
            )}
          </Stack>
        </PageHeader>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1.5, mb: 3,
          }}
        >
          <StatTile label="Total" value={stats?.total ?? null} loading={loading} />
          <StatTile label="Open" value={stats?.open ?? null} tone={stats?.open ? 'danger' : 'ok'} loading={loading} />
          <StatTile label="In progress" value={stats?.inProgress ?? null} tone="warn" loading={loading} />
          <StatTile label="Resolved" value={stats?.resolved ?? null} tone="ok" loading={loading} />
        </Box>

        <DataTable
          twoLine
          columns={columns}
          rows={reports}
          rowKey={(r) => r._id}
          loading={loading}
          rowAccent={(r) => (r.priority === 'critical' ? statusTokens.danger : undefined)}
          empty={
            <EmptyState
              compact
              icon={<BugIcon />}
              title={filtered ? 'Nothing matches those filters' : 'No bug reports'}
              description={
                filtered
                  ? 'Try a broader status or priority.'
                  : 'Reports filed from the mobile app will collect here.'
              }
              action={
                filtered
                  ? { label: 'Clear filters', onClick: () => { setStatusFilter(''); setPriorityFilter(''); } }
                  : undefined
              }
            />
          }
        />
      </PageBody>
    </>
  );
};

export default BugReports;
